import { cn } from "@/lib/utils";
import {
  Feedback,
  RequestFeedback,
} from "@/services/interfaces/IFeedbackService";
import {
  useCheckoutsFeedbacks,
  useCommunicationsFeedbacks,
  useRequestsFeedbacks,
} from "@/components/pages/feedbacks/hooks/feedbacks.hooks";
import { format } from "date-fns";
import { HandPlatter, Star } from "lucide-react";
import React from "react";
import { useTranslation } from "react-i18next";
import type { Service } from "@/types/request";
import { useAccessCapabilities } from "@/hooks/useAccessCapabilities";

interface FeedbackInfoProps {
  bookingId: string;
  guestId: string;
}

const FETCH_LIMIT = 50;

function StarRating({ rating }: { rating: number }) {
  return (
    <div className="flex gap-0.5">
      {Array.from({ length: 5 }).map((_, i) => (
        <Star
          key={i}
          className={cn(
            "size-3.5",
            i < rating
              ? "fill-light-purple text-light-purple"
              : "fill-transparent stroke-indigo",
          )}
        />
      ))}
    </div>
  );
}

function DateLine({ date }: { date: Date }) {
  return (
    <div className="flex items-center gap-1.5 text-sm font-normal">
      <span>{format(date, "dd.MM.yyyy")}</span>
      <span className="text-muted-foreground">{format(date, "HH:mm")}</span>
    </div>
  );
}

function RequestServiceChip({ service }: { service: Service }) {
  const { name, image } = service;
  if (!name) return null;
  return (
    <div className="flex w-44 items-center gap-2 rounded-lg bg-light-indigo px-2 py-1.5">
      <div className="flex size-8 shrink-0 items-center justify-center overflow-hidden rounded-md bg-neutral-200">
        {image ? (
          <img src={image} alt="" className="size-full object-cover" />
        ) : (
          <HandPlatter className="size-4 text-muted-foreground" aria-hidden />
        )}
      </div>
      <span className="min-w-0 text-xs font-medium text-violet">{name}</span>
    </div>
  );
}

function NoFeedbacks() {
  const { t } = useTranslation();
  return (
    <p className="text-xs text-muted-foreground">
      {t("reservation.feedbackSection.noFeedbacks")}
    </p>
  );
}

function SimpleFeedbackList({ feedbacks }: { feedbacks: Feedback[] }) {
  if (feedbacks.length === 0) return <NoFeedbacks />;
  return (
    <div className="flex flex-col gap-4">
      {feedbacks.map((fb) => (
        <div key={fb.id} className="flex flex-col gap-1.5">
          <div className="flex items-center gap-4">
            <DateLine date={new Date(fb.createdAt)} />
            <StarRating rating={fb.rating} />
          </div>
          {fb.comment && (
            <p className="text-xs text-neutral-500 leading-relaxed">
              &ldquo;{fb.comment}&rdquo;
            </p>
          )}
        </div>
      ))}
    </div>
  );
}

function RequestColumn({ feedbacks }: { feedbacks: RequestFeedback[] }) {
  const { t } = useTranslation();
  if (feedbacks.length === 0) return <NoFeedbacks />;
  return (
    <div className="flex flex-col gap-4">
      {feedbacks.map((fb) => (
        <div key={fb.id} className="flex flex-col gap-1.5">
          <div className="flex items-center gap-4">
            <DateLine date={new Date(fb.createdAt)} />
            <StarRating rating={fb.rating} />
          </div>
          {fb.request?.service && (
            <RequestServiceChip service={fb.request.service} />
          )}
          <p className="text-xs text-neutral-500 leading-relaxed">
            {fb.comment
              ? `\u201c${fb.comment}\u201d`
              : t("feedbacks.modal.noComments", "No comments.")}
          </p>
        </div>
      ))}
    </div>
  );
}

export const FeedbackInfo: React.FC<FeedbackInfoProps> = ({
  bookingId,
  guestId,
}) => {
  const { t } = useTranslation();
  const { hasFeedbacksAccess } = useAccessCapabilities();

  const checkoutsQuery = useCheckoutsFeedbacks(
    {
      page: 1,
      limit: FETCH_LIMIT,
    },
    hasFeedbacksAccess,
  );
  const requestsQuery = useRequestsFeedbacks(
    {
      page: 1,
      limit: FETCH_LIMIT,
    },
    hasFeedbacksAccess,
  );
  const communicationsQuery = useCommunicationsFeedbacks(
    {
      page: 1,
      limit: FETCH_LIMIT,
    },
    hasFeedbacksAccess,
  );

  if (!hasFeedbacksAccess) {
    return null;
  }

  const checkoutFeedbacks = (checkoutsQuery.data?.data ?? []).filter(
    (fb) => fb.bookingId === bookingId,
  );
  const requestFeedbacks = (requestsQuery.data?.data ?? []).filter(
    (fb) =>
      fb.request?.bookingId === bookingId || fb.request?.guestId === guestId,
  );
  const communicationFeedbacks = (communicationsQuery.data?.data ?? []).filter(
    (fb) => fb.bookingId === bookingId,
  );

  const isLoading =
    checkoutsQuery.isLoading ||
    requestsQuery.isLoading ||
    communicationsQuery.isLoading;

  if (isLoading) {
    return (
      <p className="text-xs text-muted-foreground py-2">
        {t("feedbacks.loading")}
      </p>
    );
  }

  return (
    <div className="grid grid-cols-3 divide-x divide-neutral-200 pb-2">
      <div className="flex flex-col gap-2 pr-6">
        <h4 className="text-sm font-semibold text-violet">
          {t("reservation.feedbackSection.reservation")}
        </h4>
        <SimpleFeedbackList feedbacks={checkoutFeedbacks} />
      </div>
      <div className="flex flex-col gap-2 px-6">
        <h4 className="text-sm font-semibold text-violet">
          {t("reservation.feedbackSection.request")}
        </h4>
        <RequestColumn feedbacks={requestFeedbacks} />
      </div>
      <div className="flex flex-col gap-2 pl-6">
        <h4 className="text-sm font-semibold text-violet">
          {t("reservation.feedbackSection.communication")}
        </h4>
        <SimpleFeedbackList feedbacks={communicationFeedbacks} />
      </div>
    </div>
  );
};
